// Carrusel de videos de la ficha (sección pp-videos), igual al "Spotlight" de la app Spark:
// un video grande al centro que se reproduce solo y sin sonido, y los demás asomándose a los
// lados. Se cambia deslizando, tocando un costado o con las flechas del teclado.
class PpVideos extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.pp-videos__card'));
    if (!this.cards.length) return;
    this.videos = this.cards.map((card) => card.querySelector('video'));
    this.activo = 0;
    this.visible = false;
    this.conSonido = false;
    this.enlazar();
    this.ubicar(false);
    this.classList.add('listo');

    new IntersectionObserver(
      ([entrada]) => {
        this.visible = entrada.isIntersecting;
        this.visible ? this.reproducir() : this.videos.forEach((v) => v.pause());
      },
      { threshold: 0.5 }
    ).observe(this);
    new ResizeObserver(() => this.ubicar(false)).observe(this);
    // Las portadas se piden recién cuando el carrusel se acerca a la pantalla, para no pesar al abrir la ficha.
    new IntersectionObserver(
      ([entrada], observador) => {
        if (!entrada.isIntersecting) return;
        this.videos.forEach((video) => video.dataset.poster && (video.poster = video.dataset.poster));
        observador.disconnect();
      },
      { rootMargin: '600px 0px' }
    ).observe(this);
  }

  enlazar() {
    this.videos.forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
    });
    this.cards.forEach((card, i) => {
      card.addEventListener('click', () => {
        if (this.deslizo) return;
        i === this.activo ? this.pausarOSeguir() : this.activar(i);
      });
      card.querySelector('.pp-videos__sonido')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.alternarSonido();
      });
    });

    let inicio = null;
    this.addEventListener('pointerdown', (e) => {
      inicio = { x: e.clientX, y: e.clientY };
      this.deslizo = false;
    });
    this.addEventListener('pointercancel', () => (inicio = null));
    this.addEventListener('pointerup', (e) => {
      if (!inicio) return;
      const dx = e.clientX - inicio.x;
      const dy = e.clientY - inicio.y;
      inicio = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        this.deslizo = true;
        this.activar(this.activo + (dx < 0 ? 1 : -1));
        setTimeout(() => (this.deslizo = false), 50);
      }
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.activar(this.activo + 1);
      if (e.key === 'ArrowLeft') this.activar(this.activo - 1);
    });
  }

  // Distancia de cada video al del centro. Con 3 o más, el carrusel da la vuelta para que
  // siempre haya un video asomándose a cada lado.
  distancia(i) {
    const n = this.cards.length;
    let d = i - this.activo;
    if (n >= 3) {
      d = ((d % n) + n) % n;
      if (d > n / 2) d -= n;
    }
    return d;
  }

  ubicar(animar) {
    const estilo = getComputedStyle(this);
    const gap = parseFloat(estilo.getPropertyValue('--pp-videos-gap')) || 20;
    const escala = parseFloat(estilo.getPropertyValue('--pp-videos-escala')) || 0.9;
    const ancho = this.cards[0].offsetWidth;
    const lateral = ancho * escala;
    const centro = this.clientWidth / 2;
    const sinAnimar = [];

    this.cards.forEach((card, i) => {
      const d = this.distancia(i);
      const lejos = Math.abs(d);
      const x = d === 0 ? 0 : Math.sign(d) * (ancho / 2 + gap + (lejos - 1) * (lateral + gap) + lateral / 2);
      // El video que da la vuelta de un lado al otro se mueve sin animación, para que no cruce la pantalla.
      const salto = card.dataset.d !== undefined && Math.abs(d - Number(card.dataset.d)) > 1;
      if (!animar || salto) {
        card.style.transition = 'none';
        sinAnimar.push(card);
      }
      card.style.transform = `translateX(${centro + x - ancho / 2}px) scale(${d === 0 ? 1 : escala})`;
      card.dataset.d = d;
      card.classList.toggle('is-activo', d === 0);
      card.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
    });
    if (sinAnimar.length) {
      this.offsetWidth;
      sinAnimar.forEach((card) => (card.style.transition = ''));
    }
  }

  activar(i) {
    const n = this.cards.length;
    if (n < 2) return;
    const nuevo = n >= 3 ? ((i % n) + n) % n : Math.max(0, Math.min(n - 1, i));
    if (nuevo === this.activo) return;
    this.videos[this.activo].pause();
    this.activo = nuevo;
    this.videos[nuevo].currentTime = 0;
    this.ubicar(true);
    this.reproducir();
  }

  reproducir() {
    this.classList.remove('en-pausa');
    if (!this.visible) return;
    const video = this.videos[this.activo];
    video.muted = !this.conSonido;
    video.preload = 'auto';
    video.play()?.catch(() => this.classList.add('en-pausa'));
  }

  pausarOSeguir() {
    const video = this.videos[this.activo];
    if (video.paused) {
      this.reproducir();
    } else {
      video.pause();
      this.classList.add('en-pausa');
    }
  }

  alternarSonido() {
    this.conSonido = !this.conSonido;
    this.classList.toggle('con-sonido', this.conSonido);
    this.videos[this.activo].muted = !this.conSonido;
    this.querySelectorAll('.pp-videos__sonido').forEach((boton) =>
      boton.setAttribute('aria-label', this.conSonido ? 'Quitar sonido' : 'Activar sonido')
    );
  }
}

if (!customElements.get('pp-videos')) customElements.define('pp-videos', PpVideos);
